import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, PlayCircle, ArrowRight, ArrowLeft, FileText } from "lucide-react";
import { LOGO_URL } from "../config";
import { useLang } from "../i18n";
import { custHeaders } from "../siteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WatchPage() {
  const { courseId } = useParams();
  const { lang } = useLang();
  const ar = lang === "ar";
  const navigate = useNavigate();
  const token = localStorage.getItem("themaya_customer_token") || "";
  const [grant, setGrant] = useState(undefined);
  const [current, setCurrent] = useState(null);
  const [wmPos, setWmPos] = useState({ top: "18%", insetInlineStart: "12%" });
  const [quality, setQuality] = useState("auto"); // auto | hd | sd
  const [speed, setSpeed] = useState(1);
  const resumeRef = useRef(0);

  const sdReady = current?.sd_status === "ready";
  // Auto always starts with the lightweight SD copy (smooth even on shaky connections);
  // HD is one tap away via the quality toggle for anyone who wants it.
  const effectiveQ = !sdReady ? "hd" : quality === "auto" ? "sd" : quality;

  const SPEEDS = [1, 1.25, 1.5, 2, 0.75];
  const cycleSpeed = () => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);
  const switchQuality = () => {
    resumeRef.current = videoRef.current ? videoRef.current.currentTime : 0;
    const opts = sdReady ? ["auto", "hd", "sd"] : ["auto", "hd"];
    setQuality((q) => opts[(opts.indexOf(q) + 1) % opts.length]);
  };

  const viewerEmail = useMemo(() => {
    try {
      const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(b64)).email || "";
    } catch {
      return "";
    }
  }, [token]);

  useEffect(() => {
    const t = setInterval(() => {
      setWmPos({ top: `${12 + Math.random() * 65}%`, insetInlineStart: `${8 + Math.random() * 55}%` });
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const [ticket, setTicket] = useState("");

  useEffect(() => {
    setTicket("");
    if (!current || !token) return;
    axios
      .post(`${API}/videos/${current.id}/ticket`, {}, { headers: custHeaders() })
      .then((res) => setTicket(res.data.ticket))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    const blockKeys = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "u", "p"].includes(k)) e.preventDefault();
    };
    const blockCtx = (e) => e.preventDefault();
    window.addEventListener("keydown", blockKeys);
    document.addEventListener("contextmenu", blockCtx);
    return () => {
      window.removeEventListener("keydown", blockKeys);
      document.removeEventListener("contextmenu", blockCtx);
    };
  }, []);

  const videoRef = useRef(null);
  const [shielded, setShielded] = useState(false);

  // Watch-progress heartbeat — feeds the admin Client Activity view
  useEffect(() => {
    if (!current || !token) return;
    const send = () => {
      const v = videoRef.current;
      if (!v || !v.duration) return;
      axios
        .post(`${API}/activity/progress`, { video_id: current.id, position: v.currentTime, duration: v.duration }, { headers: custHeaders() })
        .catch(() => {});
    };
    const iv = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && !v.ended) send();
    }, 15000);
    const onStop = () => send();
    const v = videoRef.current;
    v?.addEventListener("pause", onStop);
    v?.addEventListener("ended", onStop);
    return () => {
      clearInterval(iv);
      v?.removeEventListener("pause", onStop);
      v?.removeEventListener("ended", onStop);
      send();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, ticket]);

  useEffect(() => {
    const shield = () => setShielded(true);
    const unshield = () => setShielded(false);
    const onVis = () => setShielded(document.hidden);
    const onKeyUp = (e) => {
      if (e.key === "PrintScreen") {
        try {
          navigator.clipboard.writeText("");
        } catch {
          /* clipboard unavailable */
        }
      }
    };
    window.addEventListener("blur", shield);
    window.addEventListener("focus", unshield);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("blur", shield);
      window.removeEventListener("focus", unshield);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    if (shielded && videoRef.current) videoRef.current.pause();
  }, [shielded]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, ticket]);

  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/account");
      return;
    }
    axios
      .get(`${API}/my-courses`, { headers: custHeaders() })
      .then((res) => {
        const g = res.data.find((x) => x.course_id === courseId && x.active);
        setGrant(g || null);
        if (g) {
          const vids = g.videos.filter((x) => !x.kind || x.kind === "video");
          if (vids.length) setCurrent(vids[0]);
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          navigate("/account");
        } else {
          setLoadError(true); // transient server/network issue — never eject the viewer
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-luxury-black flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="text-luxury-bone text-lg">{lang === "ar" ? "تعذّر التحميل — تحققي من الاتصال بالإنترنت" : "Could not load — please check your connection"}</p>
        <button
          data-testid="watch-retry-button"
          onClick={() => window.location.reload()}
          className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors"
        >
          {lang === "ar" ? "إعادة المحاولة" : "Try again"}
        </button>
      </div>
    );
  }

  if (grant === undefined) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-luxury-gold animate-spin" />
      </div>
    );
  }

  if (!grant) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4" data-testid="watch-no-access">
        <div className="text-center border border-luxury-border bg-luxury-surface p-12 max-w-md">
          <Lock className="w-10 h-10 mx-auto mb-4 text-luxury-gold" strokeWidth={1} />
          <p className="text-luxury-bone font-serif text-2xl mb-2">{ar ? "لا تملك/تملكين صلاحية الوصول" : "No access"}</p>
          <p className="text-luxury-gray text-sm mb-6">{ar ? "هذه الدورة متاحة فقط للمشتركين والمشتركات" : "This course is available to paying members only"}</p>
          <Link to="/account" className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors inline-block">
            {ar ? "حسابي" : "My account"}
          </Link>
        </div>
      </div>
    );
  }

  const Arrow = ar ? ArrowRight : ArrowLeft;
  const lessonVideos = grant.videos.filter((v) => !v.kind || v.kind === "video");
  const pdfFiles = grant.videos.filter((v) => v.kind === "pdf");

  return (
    <div className="min-h-screen bg-luxury-black py-24 px-4 md:px-8" data-testid="watch-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="TheMaya" className="h-8" />
            <h1 className="font-serif text-3xl text-luxury-bone">{grant.course ? (ar ? grant.course.title : (grant.course.title_en || grant.course.title)) : ""}</h1>
          </div>
          <Link to="/account" data-testid="watch-back" className="flex items-center gap-2 text-luxury-gray hover:text-luxury-gold text-sm transition-colors">
            <Arrow className="w-4 h-4" strokeWidth={1.5} />
            {ar ? "دوراتي" : "My courses"}
          </Link>
        </div>

        {current ? (
          <div className="border border-luxury-border bg-luxury-surface overflow-hidden">
            <div className="relative select-none">
              <video
                ref={videoRef}
                key={`${current.id}-${ticket}-${effectiveQ}`}
                data-testid="watch-video-player"
                controls
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onLoadedMetadata={() => {
                  if (videoRef.current && resumeRef.current) {
                    videoRef.current.currentTime = resumeRef.current;
                    resumeRef.current = 0;
                  }
                  if (videoRef.current) videoRef.current.playbackRate = speed;
                }}
                className="w-full aspect-video bg-black"
                src={ticket ? `${API}/videos/${current.id}/stream?ticket=${ticket}&q=${effectiveQ}` : undefined}
              />
              {shielded && (
                <div data-testid="watch-shield" className="absolute inset-0 bg-black z-30 flex items-center justify-center">
                  <p className="text-luxury-gray text-sm px-6 text-center">{ar ? "المحتوى محمي — عُد/عودي إلى نافذة المشاهدة للمتابعة" : "Protected content — return to the watch window to continue"}</p>
                </div>
              )}
              {viewerEmail && (
                <span
                  data-testid="watch-watermark"
                  dir="ltr"
                  className="pointer-events-none absolute text-white/40 text-xs md:text-sm tracking-wider select-none transition-all duration-[2000ms] ease-in-out"
                  style={{ top: wmPos.top, insetInlineStart: wmPos.insetInlineStart }}
                >
                  {viewerEmail}
                </span>
              )}
            </div>
            <div className="p-5 border-t border-luxury-border flex items-center justify-between gap-3 flex-wrap">
              <p className="text-luxury-bone font-semibold">{current.title}</p>
              <div className="flex items-center gap-2">
                <button
                  data-testid="watch-quality-toggle"
                  onClick={switchQuality}
                  className="text-xs border border-luxury-border rounded-full px-4 py-2 text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold transition-colors"
                >
                  {ar ? "الجودة:" : "Quality:"}{" "}
                  {quality === "auto"
                    ? `${ar ? "تلقائية" : "Auto"}${sdReady ? ` (${effectiveQ.toUpperCase()})` : ""}`
                    : quality === "hd"
                      ? (ar ? "عالية HD" : "HD")
                      : (ar ? "موفّرة SD" : "SD")}
                </button>
                <button
                  data-testid="watch-speed-toggle"
                  onClick={cycleSpeed}
                  className="text-xs border border-luxury-border rounded-full px-4 py-2 text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold transition-colors"
                >
                  {ar ? "السرعة:" : "Speed:"} <span dir="ltr">{speed}x</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-luxury-gray text-center py-16">{ar ? "لا توجد فيديوهات في هذه الدورة بعد" : "No videos in this course yet"}</p>
        )}

        {lessonVideos.length > 1 && (
          <div className="mt-8 space-y-3" data-testid="watch-playlist">
            {lessonVideos.map((v, i) => (
              <button
                key={v.id}
                data-testid={`watch-video-${i}`}
                onClick={() => setCurrent(v)}
                className={`w-full flex items-center gap-4 border p-4 text-start transition-colors ${
                  current?.id === v.id ? "border-luxury-gold bg-luxury-surface" : "border-luxury-border hover:border-luxury-gold"
                }`}
              >
                <PlayCircle className="w-6 h-6 text-luxury-gold shrink-0" strokeWidth={1.25} />
                <span className="text-luxury-bone">{v.title}</span>
              </button>
            ))}
          </div>
        )}

        {pdfFiles.length > 0 && (
          <div className="mt-10" data-testid="watch-pdfs">
            <p className="text-luxury-gold text-sm mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4" strokeWidth={1.5} />
              {ar ? "ملفات الدورة (PDF)" : "Course files (PDF)"}
            </p>
            <div className="space-y-3">
              {pdfFiles.map((f, i) => (
                <a
                  key={f.id}
                  data-testid={`watch-pdf-${i}`}
                  href={`${API}/videos/${f.id}/stream?auth=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-4 border border-luxury-border p-4 hover:border-luxury-gold transition-colors"
                >
                  <FileText className="w-6 h-6 text-luxury-gold shrink-0" strokeWidth={1.25} />
                  <span className="text-luxury-bone flex-1 text-start">{f.title}</span>
                  <span className="text-luxury-gray text-xs">{ar ? "فتح الملف" : "Open file"}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
